import { Box, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { faArrowRight, faListCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Checkbox,
  FormFeedback,
  Modal,
  useToast
} from '@morressier/alembic';
import {
  CheckTypeMap,
  ECheckType,
  ICheckResultResponse
} from '@publishing-engine/domain/models';

import { Controller, useForm } from 'react-hook-form';
import { RunSelectedCheck } from 'src/api/mutations';
import ListView from 'src/layouts/ListView';

type IProps = {
  Integrity: ICheckResultResponse;
  runCheckOpen: boolean;
  onClose: () => void;
};
const RunChecksModal = ({ Integrity, runCheckOpen, onClose }: IProps) => {
  const { mutateAsync, isPending } = RunSelectedCheck(Integrity.submissionId);
  const toast = useToast();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      checkboxList: [] as string[]
    }
  });
  const selectedChecks = watch('checkboxList');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (checked) {
      setValue('checkboxList', [...selectedChecks, name]);
    } else {
      setValue(
        'checkboxList',
        selectedChecks.filter(item => item !== name)
      );
    }
  };
  const RunSelectedChecks: () => void = async () => {
    try {
      const response = await mutateAsync({
        submissionId: Integrity.submissionId,
        revisionNumber: Integrity.roundId,
        checks: selectedChecks as ECheckType[],
        runId: Integrity.checkResults[0]?.runId!
      });
      if (response?.isSuccess && response?.externalId) {
        toast({
          variant: 'success',
          title: 'Selected integrity checks have been applied',
          description: `New check result will be available soon`,
          status: 'success'
        });
      } else if (response?.message) {
        toast({
          variant: 'attention',
          description: response?.message,
          status: 'warning'
        });
      }
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error updating',
        description: 'An error occurred while Checks Activated',
        status: 'error'
      });
    } finally {
      setValue('checkboxList', []);
      onClose();
    }
  };
  const ManualCheckRun = Integrity?.checkResults?.filter(
    item => item?.runType === 'manual'
  );
  const selectAllChecks = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (checked) {
      setValue(
        'checkboxList',
        ManualCheckRun.filter(
          item =>
            (!item.externalId && item.status === 'PROCESSING') ||
            item.status === 'NOT_STARTED'
        ).map(item => item.checkType)
      );
    } else {
      setValue('checkboxList', []);
    }
  };
  return (
    <Modal isOpen={runCheckOpen} onClose={onClose} width="720px">
      <form onSubmit={handleSubmit(RunSelectedChecks)}>
        <ListView>
          <ListView.Title>
            <Flex alignItems={'center'} justifyContent={'space-between'}>
              <Flex gap={4} alignItems={'center'}>
                <FontAwesomeIcon icon={faListCheck} />
                <Text textStyle="headingGroup"> Run Integrity Checks</Text>
              </Flex>
              <Box>
                {ManualCheckRun.length >= 1 && (
                  <Checkbox
                    id={'SelectAll'}
                    name={'AllChecks'}
                    ariaLabel={`Select All `}
                    lineHeight={0}
                    isChecked={
                      selectedChecks.length ===
                      ManualCheckRun.filter(
                        item =>
                          (!item.externalId && item.status === 'PROCESSING') ||
                          item.status === 'NOT_STARTED'
                      ).length
                    }
                    disabled={isPending}
                    onChange={e => selectAllChecks(e)}
                  >
                    Select All
                  </Checkbox>
                )}
              </Box>
            </Flex>
            <Flex
              flexDirection="column"
              alignItems="start"
              marginTop={4}
              gap={2}
            ></Flex>
          </ListView.Title>
          <ListView.Body>
            {ManualCheckRun.length >= 1 ? (
              <>
                <SimpleGrid
                  columns={2}
                  spacing={4}
                  marginBottom={4}
                  alignItems="start"
                >
                  {ManualCheckRun.map(item => {
                    const name = item.checkType;
                    return (
                      <>
                        <Flex key={item.roundId} gap={3} w="100%">
                          <Controller
                            name="checkboxList"
                            control={control}
                            rules={{
                              validate: value =>
                                value?.length > 0 ||
                                'Please select at least one check to proceed.'
                            }}
                            render={() => (
                              <Checkbox
                                id={item.roundId}
                                name={name}
                                ariaLabel={`Select ${name}`}
                                isChecked={selectedChecks?.includes(name)}
                                onChange={e => handleChange(e)}
                                disabled={
                                  Boolean(
                                    (item.externalId &&
                                      item.status === 'PROCESSING') ||
                                      item.status === 'COMPLETE'
                                  ) || isPending
                                }
                              >
                                {item.result?.title
                                  ? item.result.title
                                  : CheckTypeMap[item.checkType]}
                              </Checkbox>
                            )}
                          />
                        </Flex>
                      </>
                    );
                  })}
                </SimpleGrid>
                {errors.checkboxList && (
                  <FormFeedback variant="error">
                    {errors.checkboxList.message}
                  </FormFeedback>
                )}
              </>
            ) : (
              <>
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor="neutralSecondaryFill"
                  borderRadius="small"
                  height="112px"
                >
                  <FontAwesomeIcon icon={faListCheck} size="lg" />
                </Flex>
                <Flex flexDirection="column" alignItems="center" marginTop={4}>
                  <Text textStyle="headingGroup">
                    No Manual Checks Run Available
                  </Text>
                </Flex>
              </>
            )}
          </ListView.Body>

          {ManualCheckRun.length >= 1 && (
            <Flex
              justifyContent="flex-end"
              gap={2}
              alignItems="center"
              marginTop={4}
              width="100%"
            >
              <Button onClick={onClose} variant="secondary">
                Close
              </Button>
              <Button
                rightIcon={
                  isPending ? (
                    <Spinner />
                  ) : (
                    <FontAwesomeIcon icon={faArrowRight} />
                  )
                }
                disabled={isPending}
                variant="primary"
                type="submit"
              >
                {'Run Selected Checks'}
              </Button>
            </Flex>
          )}
        </ListView>
      </form>
    </Modal>
  );
};

export default RunChecksModal;
